import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Assignment } from '../../api/endpoints.js';
import { currentLocale } from '../../i18n/index.js';
import { testName } from './status.js';

/**
 * Module-local presentation pieces for the work pages and the dashboards
 * (D278 redesign). Presentation only: nothing here fetches, decides or
 * computes a figure — every value drawn is one the caller already holds.
 */

// ---------------------------------------------------------------------------
// View tabs — the prototype's "views" strip
// ---------------------------------------------------------------------------

export interface ViewTab<Value extends string> {
  readonly value: Value;
  /** Already translated. */
  readonly label: string;
  /** A real count the caller holds. Omitted when there is none — never 0. */
  readonly count?: string;
}

/**
 * A row of view tabs: an ARIA tablist with roving focus.
 *
 * Arrow keys follow the READING direction — under Arabic the next tab is to
 * the left — and Home / End jump to the ends. Selecting a tab is the same act
 * as choosing the view in the page's old select, so the caller's state (and
 * its query) is unchanged.
 */
export function ViewTabs<Value extends string>({
  label,
  idBase,
  value,
  tabs,
  onChange,
}: {
  /** Names the tab list for assistive technology. Already translated. */
  readonly label: string;
  readonly idBase: string;
  readonly value: Value;
  readonly tabs: readonly ViewTab<Value>[];
  readonly onChange: (value: Value) => void;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const isRtl =
      typeof document !== 'undefined' && document.documentElement.getAttribute('dir') === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';
    let next: number | null = null;
    if (event.key === forward) next = (index + 1) % tabs.length;
    else if (event.key === backward) next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    const target = tabs[next];
    if (!target) return;
    onChange(target.value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex flex-wrap gap-x-1 border-b border-default-200"
    >
      {tabs.map((tab, index) => {
        const isSelected = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(element) => {
              refs.current[index] = element;
            }}
            id={`${idBase}-tab-${tab.value}`}
            type="button"
            role="tab"
            aria-selected={isSelected}
            aria-controls={`${idBase}-panel`}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => {
              if (!isSelected) onChange(tab.value);
            }}
            onKeyDown={(event) => move(event, index)}
            className={`-mb-px inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 pb-2 pt-2 text-caption font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 ${
              isSelected
                ? 'border-primary-600 text-default-900'
                : 'border-transparent text-default-600 hover:text-default-900'
            }`}
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span className="measure rounded-full border border-default-200 bg-default-50 px-1.5 text-micro font-medium text-default-700">
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** The region a `ViewTabs` strip controls. */
export function ViewTabPanel({
  idBase,
  value,
  children,
}: {
  readonly idBase: string;
  readonly value: string;
  readonly children: ReactNode;
}) {
  return (
    <div role="tabpanel" id={`${idBase}-panel`} aria-labelledby={`${idBase}-tab-${value}`}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The work cell — sample number (a link to the record) over the test name
// ---------------------------------------------------------------------------

/**
 * Sample ID (and piece) over the test's name, the record's own link.
 *
 * The number is a machine identifier: always left-to-right. The link is the
 * row's way into its record; the page's next-step button stays beside it.
 */
export function WorkCell({
  assignment,
  to,
}: {
  readonly assignment: Pick<Assignment, 'sample' | 'pieceCode' | 'test'>;
  /** The record the row opens. */
  readonly to: string;
}) {
  const locale = currentLocale();
  return (
    <div className="min-w-0">
      <Link
        to={to}
        className="measure block font-medium text-default-900 no-underline hover:text-primary-700 hover:underline"
        dir="ltr"
      >
        {assignment.sample.sampleNumber}
        {assignment.pieceCode ? ` / ${assignment.pieceCode}` : ''}
      </Link>
      <span className="block truncate text-caption text-default-600">
        {testName(assignment.test, locale)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Horizontal bars — counts the server sent, nothing derived
// ---------------------------------------------------------------------------

export type BarTone = 'magnitude' | 'warning' | 'danger';

export interface BarDatum {
  readonly key: string;
  /** Already translated. */
  readonly label: string;
  /** The recorded count. A section the server withheld is never a datum. */
  readonly value: number;
  /** The list this count counts. */
  readonly to?: string;
  /** `magnitude` (sage) unless the bar itself means a state. */
  readonly tone?: BarTone;
  /** A second recorded figure shown beside the value. Already formatted. */
  readonly detail?: string;
  /** The same figure for the table equivalent's own column. Already formatted. */
  readonly detailValue?: string;
  /** Inks the detail in the warning colour (it means a late or held state). */
  readonly detailIsAttention?: boolean;
}

const BAR_FILL: Record<BarTone, string> = {
  magnitude: 'bg-primary-600',
  warning: 'bg-warning-600',
  danger: 'bg-danger-600',
};

/**
 * Plain horizontal bars with a visible value on each, and the same figures as
 * a table behind a "View as table" disclosure.
 *
 * The length is the count relative to the largest count shown — a drawing of
 * the numbers, not a rate. Each bar with a list is a link to that list, and
 * the link's text carries the label and the value, so the bar itself is
 * decoration for assistive technology.
 */
export function HorizontalBars({
  data,
  format,
  labelHeader,
  valueHeader,
  caption,
  detailHeader,
}: {
  readonly data: readonly BarDatum[];
  readonly format: (value: number) => string;
  /** Column headers of the table equivalent. Already translated. */
  readonly labelHeader: string;
  readonly valueHeader: string;
  /** Names the table equivalent. Already translated. */
  readonly caption: string;
  /** The table equivalent's header for `detail`, when the data carries one. */
  readonly detailHeader?: string;
}) {
  const { t } = useTranslation();
  const max = Math.max(1, ...data.map((item) => item.value));

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-0.5">
        {data.map((item) => {
          const width = item.value === 0 ? 0 : Math.max(2, (item.value / max) * 100);
          const body = (
            <>
              <span className="min-w-0 truncate text-caption text-default-800">{item.label}</span>
              <span
                aria-hidden="true"
                className="h-2.5 overflow-hidden rounded-e-full bg-default-100"
              >
                <span
                  className={`block h-full rounded-e-full ${BAR_FILL[item.tone ?? 'magnitude']}`}
                  style={{ width: `${width}%` }}
                />
              </span>
              <span className="flex items-baseline justify-end gap-2 whitespace-nowrap">
                <span className="measure min-w-8 text-end text-caption font-semibold text-default-900">
                  {format(item.value)}
                </span>
                {item.detail !== undefined ? (
                  <span
                    className={`text-micro ${item.detailIsAttention ? 'font-semibold text-warning-700' : 'text-default-600'}`}
                  >
                    {item.detail}
                  </span>
                ) : null}
              </span>
            </>
          );
          const layout =
            'grid grid-cols-[minmax(6rem,38%)_minmax(0,1fr)_auto] items-center gap-2.5 rounded-medium px-1.5 py-1';
          return (
            <li key={item.key}>
              {item.to ? (
                <Link
                  to={item.to}
                  className={`${layout} text-default-900 no-underline outline-none hover:bg-default-50 focus-visible:ring-2 focus-visible:ring-primary-500`}
                >
                  {body}
                </Link>
              ) : (
                <div className={layout}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
      <details className="text-caption">
        <summary className="w-fit cursor-pointer rounded-medium px-1.5 py-0.5 font-medium text-primary-700 hover:bg-default-50">
          {t('dashboards.viewAsTable')}
        </summary>
        {/* Named by aria-label rather than a caption, so the panel's title
            is not repeated as a second visible-to-search text. */}
        <table aria-label={caption} className="mt-2 w-full border-collapse text-caption">
          <thead>
            <tr className="border-b border-default-200 bg-default-50 text-default-700">
              <th scope="col" className="px-2 py-1.5 text-start font-semibold">
                {labelHeader}
              </th>
              <th scope="col" className="px-2 py-1.5 text-end font-semibold">
                {valueHeader}
              </th>
              {detailHeader ? (
                <th scope="col" className="px-2 py-1.5 text-end font-semibold">
                  {detailHeader}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.key} className="border-b border-default-100 last:border-b-0">
                <th scope="row" className="px-2 py-1.5 text-start font-normal text-default-800">
                  {item.label}
                </th>
                <td className="measure px-2 py-1.5 text-end text-default-900">{format(item.value)}</td>
                {detailHeader ? (
                  <td className="measure px-2 py-1.5 text-end text-default-900">{item.detailValue ?? item.detail}</td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Count rows — a figure beside its label, each linking to its list
// ---------------------------------------------------------------------------

export interface CountRow {
  /** Already translated. */
  readonly label: string;
  /** Already formatted. */
  readonly value: string;
  readonly to: string;
  /**
   * When set and the figure is above zero, the value is inked in the state's
   * colour. A zero stays neutral: nothing is waiting.
   */
  readonly attention?: 'warning' | 'danger';
  readonly isAboveZero?: boolean;
  /** Money and other mixed text read better in body size. */
  readonly isText?: boolean;
}

const ATTENTION_INK = {
  warning: 'text-warning-700',
  danger: 'text-danger-700',
} as const;

export function CountRows({ items }: { readonly items: readonly CountRow[] }) {
  return (
    <dl className="flex flex-col divide-y divide-default-100">
      {items.map((item) => (
        <div key={item.label} className="flex items-baseline justify-between gap-stack py-2">
          <dt className="min-w-0 text-body text-default-800">
            <Link to={item.to} className="text-default-800 no-underline hover:text-primary-700 hover:underline">
              {item.label}
            </Link>
          </dt>
          <dd
            className={`measure font-semibold ${item.isText ? 'text-body' : 'text-section'} ${
              item.attention && item.isAboveZero ? ATTENTION_INK[item.attention] : 'text-default-900'
            }`}
            dir="ltr"
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
